import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { consumableMaterialService } from '../services/api';
import PageBackHeader from '../components/PageBackHeader';
import NomenclatureUsageDialog, { checkNomenclatureDelete } from '../components/NomenclatureUsageDialog';

function formatDrfErrors(data) {
  if (data == null) return null;
  if (typeof data === 'string') return data;
  if (data.detail != null) return Array.isArray(data.detail) ? data.detail.join('\n') : String(data.detail);
  const lines = [];
  Object.keys(data).forEach((key) => {
    const v = data[key];
    if (v == null) return;
    if (Array.isArray(v)) {
      const text = v.join(' ');
      lines.push(key === 'name' || key === 'non_field_errors' ? text : `${key}: ${text}`);
    } else if (key === 'name') {
      lines.push(String(v));
    } else {
      lines.push(`${key}: ${v}`);
    }
  });
  return lines.length ? lines.join('\n') : JSON.stringify(data);
}

const EMPTY_FORM = {
  name: '', supplier_name: '', article: '', supplier_article: '',
  type: '', description: '', minimum_stock: '', unit_price: '',
};

function ConsumableMaterialPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [usageDialog, setUsageDialog] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await consumableMaterialService.getAll();
      setItems(res.data.results || res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (item = null) => {
    if (item) {
      setEditing(item.id);
      setFormData({
        name: item.name || '',
        supplier_name: item.supplier_name || '',
        article: item.article || '',
        supplier_article: item.supplier_article || '',
        type: item.type || '',
        description: item.description || '',
        minimum_stock: item.minimum_stock != null ? String(item.minimum_stock) : '',
        unit_price: item.unit_price != null ? String(item.unit_price) : '',
      });
    } else {
      setEditing(null);
      setFormData(EMPTY_FORM);
    }
    setOpen(true);
  };

  const handleClose = () => { setOpen(false); setEditing(null); };

  const handleSubmit = async () => {
    if (saving) return;
    try {
      const upStr = String(formData.unit_price ?? '').trim().replace(',', '.');
      let unit_price = null;
      if (upStr !== '') {
        const n = parseFloat(upStr);
        if (!Number.isFinite(n) || n < 0) { alert('Вкажіть коректну ціну або залиште порожнім'); return; }
        unit_price = n;
      }
      const articleTrimmed = String(formData.article ?? '').trim();
      const articleNorm = articleTrimmed === '' ? null : articleTrimmed;
      const nameTrim = (formData.name || '').trim().toLowerCase();
      if (nameTrim) {
        const nameDup = items.find(
          (m) =>
            (m.name || '').trim().toLowerCase() === nameTrim &&
            (!editing || Number(m.id) !== Number(editing)),
        );
        if (nameDup) {
          alert('Така назва вже існує');
          return;
        }
      }
      const payload = {
        name: formData.name,
        supplier_name: formData.supplier_name || '',
        article: articleNorm,
        supplier_article: formData.supplier_article || '',
        type: formData.type || '',
        description: formData.description || '',
        unit_price,
        minimum_stock: formData.minimum_stock === '' || formData.minimum_stock == null ? null : Number(formData.minimum_stock),
      };
      setSaving(true);
      if (editing) {
        await consumableMaterialService.update(editing, payload);
      } else {
        await consumableMaterialService.create(payload);
      }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.message || 'Помилка збереження';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const item = items.find((x) => x.id === id);
    const itemName = item?.name || '';
    try {
      const check = await checkNomenclatureDelete(consumableMaterialService, id, {
        itemName,
        confirmMessage: 'Видалити цей розхідний матеріал?',
      });
      if (check.blocked) {
        setUsageDialog({ usage: check.usage, itemName: check.itemName || itemName });
        return;
      }
      if (!check.confirmed) return;
      await consumableMaterialService.delete(id);
      loadData();
    } catch (e) {
      const data = e.response?.data;
      if (e.response?.status === 409 && data?.in_use) {
        setUsageDialog({ usage: data, itemName: data.name || itemName });
        return;
      }
      alert(formatDrfErrors(data) || 'Помилка видалення');
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  return (
    <Box>
      <PageBackHeader />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Розхідні матеріали</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Додати матеріал
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Назва у постачальника</TableCell>
              <TableCell>Артикул</TableCell>
              <TableCell>Артикул постачальника</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Ціна (грн)</TableCell>
              <TableCell>Мін. залишок</TableCell>
              <TableCell>Опис</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.supplier_name || '—'}</TableCell>
                <TableCell>{item.article || '—'}</TableCell>
                <TableCell>{item.supplier_article || '—'}</TableCell>
                <TableCell>{item.type || '—'}</TableCell>
                <TableCell>{item.unit_price != null ? item.unit_price : '—'}</TableCell>
                <TableCell>{item.minimum_stock != null ? item.minimum_stock : '—'}</TableCell>
                <TableCell>{item.description || '—'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(item)}><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDelete(item.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати матеріал' : 'Додати розхідний матеріал'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField label="Назва" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth />
            <TextField label="Назва у постачальника" value={formData.supplier_name} onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })} fullWidth />
            <TextField label="Артикул" value={formData.article} onChange={(e) => setFormData({ ...formData, article: e.target.value })} fullWidth helperText="Унікальний; порожньо — без артикула" />
            <TextField label="Артикул постачальника" value={formData.supplier_article} onChange={(e) => setFormData({ ...formData, supplier_article: e.target.value })} fullWidth />
            <TextField label="Тип" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} fullWidth />
            <TextField label="Ціна (грн)" type="number" inputProps={{ min: 0, step: 'any' }} value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })} fullWidth />
            <TextField label="Мінімальний залишок" type="number" inputProps={{ min: 0, step: 'any' }} value={formData.minimum_stock} onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })} fullWidth helperText="Порожньо — без порогу" />
            <TextField label="Опис" multiline rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <NomenclatureUsageDialog
        open={!!usageDialog}
        onClose={() => setUsageDialog(null)}
        usage={usageDialog?.usage}
        itemName={usageDialog?.itemName}
      />
    </Box>
  );
}

export default ConsumableMaterialPage;
